/**
 * Subscription plans, read live from the platform.
 *
 * `GET /api/v1/billing/plans` is the one product endpoint open to anonymous
 * callers, which is exactly what a pricing section needs: no price is typed
 * into this page, so none can drift away from what the billing service would
 * actually charge. When the call fails the section says so and points at
 * registration - it never falls back to remembered numbers.
 *
 * `supports_downloads` is deliberately not rendered. It is a real entitlement
 * flag on the plan, but neither the web player nor the Roku channel implements
 * offline downloads, so printing it here would sell a feature that does not
 * exist. Add it to the feature list on the day a client can honour it.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError, request } from "@/api/client";
import type { Plan, PlansResponse } from "@/api/types";

/** The plan `signUp` sends for a new account (see state/session.tsx). */
const DEFAULT_PLAN_CODE = "standard";

const RESOLUTION_LABELS: Record<string, string> = {
  "720p": "HD, 720p",
  "1080p": "Full HD, 1080p",
  "1440p": "Quad HD, 1440p",
  "2160p": "4K where the title has it",
};

function formatPrice(plan: Plan): string {
  const amount = Number(plan.price);
  if (!Number.isFinite(amount)) return plan.price;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: plan.currency || "USD",
      minimumFractionDigits: 2,
    }).format(amount);
  } catch {
    // An unexpected currency code must not take the pricing section down.
    return `${plan.currency} ${plan.price}`;
  }
}

function features(plan: Plan): string[] {
  return [
    plan.max_streams === 1 ? "One stream at a time" : `${plan.max_streams} streams at a time`,
    `Up to ${plan.max_profiles} profiles`,
    RESOLUTION_LABELS[plan.max_resolution] ?? plan.max_resolution,
    plan.has_ads ? "Ad supported" : "No ads",
  ];
}

export function PlanGrid() {
  const [plans, setPlans] = useState<Plan[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);

  const retry = useCallback(() => setAttempt((value) => value + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setFailed(false);
    // Anonymous on purpose: a visitor has no token, and a stale one left in
    // storage must not turn a public price list into a refresh attempt.
    request<PlansResponse>("/api/v1/billing/plans", {
      anonymous: true,
      signal: controller.signal,
    })
      .then((payload) => {
        setPlans(payload.plans ?? []);
        setLoading(false);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        if (cause instanceof ApiError || cause instanceof Error) setFailed(true);
        setLoading(false);
      });
    return () => controller.abort();
  }, [attempt]);

  if (loading) {
    return (
      <div className="plans" aria-busy="true">
        <span className="visually-hidden">Loading plans</span>
        {[0, 1, 2].map((index) => (
          <div key={index} className="plan plan--skeleton" aria-hidden="true">
            <div className="skeleton plan__skeleton-line plan__skeleton-line--sm" />
            <div className="skeleton plan__skeleton-line plan__skeleton-line--lg" />
            <div className="skeleton plan__skeleton-line" />
            <div className="skeleton plan__skeleton-line" />
          </div>
        ))}
      </div>
    );
  }

  if (failed || !plans || plans.length === 0) {
    return (
      <div className="plans__fallback panel">
        <h3>Plan prices are not loading right now</h3>
        <p>
          Rather than show you a number we cannot verify, we would rather show you none. Current
          prices, stream counts and picture quality are confirmed before you are asked for anything,
          and the first fourteen days are free either way.
        </p>
        <div className="cta-row">
          <Link to="/signup" className="btn btn--accent">
            Create your account
          </Link>
          <button type="button" className="btn btn--ghost" onClick={retry}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <ul className="plans">
      {plans.map((plan) => {
        const isDefault = plan.code === DEFAULT_PLAN_CODE;
        return (
          <li key={plan.code} className={isDefault ? "plan plan--default" : "plan"}>
            <div className="plan__head">
              <h3 className="plan__name">{plan.name}</h3>
              {isDefault && <span className="chip chip--accent">Starts here by default</span>}
            </div>
            <p className="plan__price">
              <span className="plan__amount">{formatPrice(plan)}</span>
              <span className="plan__interval"> per {plan.interval}</span>
            </p>
            <ul className="plan__features">
              {features(plan).map((feature) => (
                <li key={feature}>{feature}</li>
              ))}
            </ul>
            {/* Three links to /signup need three distinct accessible names, or a
                screen-reader link list reads "Choose" three times. */}
            <Link
              to="/signup"
              className={isDefault ? "btn btn--accent btn--block" : "btn btn--ghost btn--block"}
            >
              Choose {plan.name}
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
